// stores/useCustomerStore.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { customerApi, styleApi } from '@/api'

export const useCustomerStore = defineStore('customer', () => {
  // ===== State =====
  const customerList = ref([])
  const customerStylesCache = ref({})
  const searchKeyword = ref('')
  const dataLoaded = ref(false)

  // ✅ 分页信息
  const pagination = ref({
    page: 1,
    pageSize: 9999,
    total: 0,
    totalPages: 0
  })

  // ===== Getters（只保留需要计算的） =====
  const filteredCustomer = computed(() => {
    if (!searchKeyword.value) return customerList.value
    const keyword = searchKeyword.value.toLowerCase()
    return customerList.value.filter(c =>
      c.name.toLowerCase().includes(keyword)
    )
  })

  const getCustomerStyles = computed(() => {
    return (customerId) => {
      if (!customerId) return []
      return customerStylesCache.value[customerId] || []
    }
  })

  function clearCustomerStylesCache(customerId) {
    if (customerId !== undefined) {
      delete customerStylesCache.value[customerId]
    } else {
      customerStylesCache.value = {}
    }
  }

  // ✅ 加载客户（支持分页 + 搜索）
  async function loadCustomer(params = {}) {
    try {
      const finalParams = {
        search: params.search !== undefined ? params.search : searchKeyword.value,
        page: params.page || pagination.value.page,
        pageSize: params.pageSize || pagination.value.pageSize,
      }

      if (params.search !== undefined) {
        searchKeyword.value = params.search
        pagination.value.page = 1
      }

      const data = await customerApi.getCustomer(finalParams)
      if (data && data.success) {
        customerList.value = data.customer || []
        if (data.pagination) {
          pagination.value = data.pagination
        }
        dataLoaded.value = true
        return customerList.value
      }
      return []
    } catch (err) {
      console.error('加载客户列表失败:', err)
      return []
    }
  }

  // ✅ 切换页码
  async function goToPage(page) {
    pagination.value.page = page
    return await loadCustomer()
  }

  // ✅ 搜索客户
  async function searchCustomer(keyword) {
    return await loadCustomer({ search: keyword })
  }

  async function loadCustomerStyles(customerId) {
    try {
      const data = await styleApi.getCustomerStyles(customerId)
      if (data && data.success) {
        const styles = data.data || []
        customerStylesCache.value[customerId] = styles
        return styles
      }
      return []
    } catch (err) {
      console.error('加载客户款式失败:', err)
      return []
    }
  }

  async function addCustomer(name) {
    try {
      const result = await customerApi.addCustomer(name, false)
      if (result?.customer) {
        await loadCustomer()
        return result.customer
      }
      return null
    } catch (err) {
      console.error('新增客户失败:', err)
      throw err
    }
  }

  async function updateCustomer(id, name) {
    try {
      const result = await customerApi.updateCustomer(id, name, false)
      if (result && result.success) {
        await loadCustomer()
        const customer = customerList.value.find(c => c.id === id)
        if (customer) {
          customer.name = name
        }
        return true
      }
      return false
    } catch (err) {
      console.error('更新客户失败:', err)
      throw err
    }
  }

  async function deleteCustomer(id) {
    try {
      const result = await customerApi.deleteCustomer(id)
      if (result && result.success) {
        clearCustomerStylesCache(id)
        await loadCustomer()
        return true
      }
      return false
    } catch (err) {
      console.error('删除客户失败:', err)
      throw err
    }
  }

  async function updateCustomerStyles(customerId, styleIds) {
    try {
      const result = await styleApi.updateCustomerStyles(customerId, styleIds)
      if (result && result.success) {
        await loadCustomerStyles(customerId)
        return true
      }
      return false
    } catch (err) {
      console.error('更新客户款式失败:', err)
      throw err
    }
  }

  // ✅ 修改：一次批量请求，替代原来的 N 次循环
  async function loadAllCustomerStyles() {
    try {
      const res = await styleApi.getAllCustomerStyles()
      // 后端返回 { data: { [customerId]: [styles] } }
      customerStylesCache.value = res?.data || {}
      return customerStylesCache.value
    } catch (err) {
      console.error('批量加载客户款式失败:', err)
      return {}
    }
  }

  return {
    // State（直接暴露 ref）
    customerList,
    customerStylesCache,
    searchKeyword,
    dataLoaded,
    pagination,

    // Getters
    filteredCustomer,
    getCustomerStyles,

    // Actions（只保留有业务逻辑的方法）
    clearCustomerStylesCache,
    loadCustomer,
    goToPage,
    searchCustomer,
    loadCustomerStyles,
    loadAllCustomerStyles,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    updateCustomerStyles,
  }
})